import React from 'react';

import { getCurrentEntityPendingChanges } from 'app/pages/loanApplication/LoanApplication.utils';
import _ from 'lodash';

import { CreditTypeEnum } from 'constants/loanApplication';
import { creditOptions } from 'constants/options';
import {
  HistoryTypeToPendingChangesTypeMap,
  PendingChangesActionType,
} from 'constants/pendingChanges';
import { mergePendingChanges } from 'utils/pendingChanges';

import CreditForm from './CreditForm';
import { creditModifiedFieldsToExclude } from './Credits.constants';

export const createNewTableData = () => {
  return {
    id: _.uniqueId(),
    // * This flag is needed for cases when we add new entity but not save it
    isNewlyAdded: true,
    isAdded: true,
  };
};

export const normalizeFormData = ({ form, initialData }) => {
  return {
    id: initialData.id,
    isAdded: initialData.isAdded,
    creditType: form.creditType.value,
    purchaseCreditType: form.purchaseCreditType?.value,
    description: form.description,
    sourceType: form.sourceType?.value,
    otherDescription: form.otherDescription,
    amount: form.amount,
  };
};

export const prepareModifiedFields = ({ currentData, pendingChangesData, initialValuesMap }) =>
  currentData.map(values => {
    const pendingChanges = pendingChangesData[values.id];
    const currentPendingChanges = getCurrentEntityPendingChanges({
      initialValues: initialValuesMap[values.id],
      currentValues: values,
    });

    if (!pendingChanges && _.isEmpty(currentPendingChanges)) {
      return {
        ...values,
        pendingChangesActionType: PendingChangesActionType.NO_CHANGES,
      };
    }

    const mergedPendingChanges = _.omit(
      mergePendingChanges(pendingChanges, currentPendingChanges),
      ['isSeparateChange'],
    );

    const modifiedFields = Object.keys(mergedPendingChanges);

    const normalizedModifiedFields = modifiedFields.reduce((acc, field) => {
      const pendingChange = mergedPendingChanges[field];

      if (pendingChange.newValue === pendingChange.initialValue) {
        return acc;
      }

      if (creditModifiedFieldsToExclude.includes(field)) {
        return acc;
      }

      acc.push(_.upperFirst(field));

      return acc;
    }, []);

    const historyActionType = modifiedFields[0]
      ? mergedPendingChanges[modifiedFields[0]]?.actionType
      : '';
    const pendingChangesActionType = HistoryTypeToPendingChangesTypeMap[historyActionType];

    return {
      ...values,
      modifiedFields: normalizedModifiedFields,
      pendingChangesActionType,
    };
  });

export const addFormToTableData = ({
  tableData,
  editData,
  cancelForm,
  isLoading,
  pendingChanges,
  saveForm,
  creditsList,
}) => {
  return tableData.map(item =>
    item.id === editData.id
      ? {
          id: item.id,
          creditType: {
            position: 1,
            colSpan: 6,
            content: (
              <CreditForm
                cancelForm={cancelForm}
                creditData={editData}
                creditsList={creditsList}
                isLoading={isLoading}
                pendingChanges={pendingChanges[item.id]}
                saveForm={saveForm}
              />
            ),
          },
          isAdded: editData.isAdded,
        }
      : item,
  );
};

export const getAvailableCreditOptions = ({
  creditsList,
  selectedValue,
  hasUserPermissionToEditLenderCredit,
  isFHARefinance,
}) =>
  creditOptions.reduce((acc, option) => {
    if (
      !hasUserPermissionToEditLenderCredit &&
      option.value === CreditTypeEnum.LENDER_CREDIT &&
      selectedValue !== CreditTypeEnum.LENDER_CREDIT
    ) {
      return acc;
    }

    if (
      option.value === CreditTypeEnum.PURCHASE_CREDIT ||
      option.value === CreditTypeEnum.OTHER_CREDIT ||
      option.value === selectedValue
    ) {
      acc.push(option);

      return acc;
    }

    if (!isFHARefinance && option.value === CreditTypeEnum.FHA_MI_CREDIT) {
      return acc;
    }

    if (!creditsList.some(({ creditType }) => creditType === option.value)) {
      acc.push(option);
    }

    return acc;
  }, []);
